import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { QueryClient } from '@tanstack/react-query'

export type ConsoleLogLevel = 'log' | 'info' | 'warn' | 'error'

export type ConsoleLogEntry = {
  level: ConsoleLogLevel
  message: string
  timestamp: string
}

export type NetworkLogEntry = {
  id: string
  type: 'fetch' | 'xhr' | 'resource'
  url: string
  method: string
  status?: number
  ok?: boolean
  durationMs?: number
  timestamp: string
  initiator?: string
  transferSize?: number
  encodedBodySize?: number
  decodedBodySize?: number
  error?: string
}

type MemoryInfo = {
  jsHeapSizeLimit: number
  totalJSHeapSize: number
  usedJSHeapSize: number
}

type NetworkInformationLike = {
  downlink?: number
  effectiveType?: string
  rtt?: number
  saveData?: boolean
}

export type DiagnosticsSnapshot = {
  url: string
  userAgent: string
  viewport: string
  timezone?: string
  language?: string
  platform?: string
  online: boolean
  referrer?: string
  timestamp: string
  memory?: string
  memoryStats?: {
    usedJSHeapSize?: number
    totalJSHeapSize?: number
    jsHeapSizeLimit?: number
  }
  connection?: {
    effectiveType?: string
    downlink?: number
    rtt?: number
    saveData?: boolean
  }
  navigation?: {
    type?: string
    loadEventEnd?: number
    domContentLoaded?: number
    domInteractive?: number
    transferSize?: number
    encodedBodySize?: number
    decodedBodySize?: number
  }
  resourceCounts?: {
    total: number
    scripts: number
    images: number
    fetches: number
    other: number
  }
  tanstackQuerySummary?: {
    activeQueries: number
    errorQueries: number
    fetchingQueries: number
    queries: Array<{ queryKey: string; state: string; dataUpdatedAt?: number }>
  }
  failedRequests?: Array<{
    url: string
    method: string
    status?: number
    error?: string
    timestamp: string
  }>
}

type ConsoleMethod = (...args: unknown[]) => void

const MAX_LOG_ENTRIES = 200
const MAX_NETWORK_ENTRIES = 200

const createId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `log-${Date.now()}-${Math.random().toString(16).slice(2)}`

export function useClientDiagnostics(queryClient?: QueryClient) {
  const [consoleLogs, setConsoleLogs] = useState<ConsoleLogEntry[]>([])
  const [networkLogs, setNetworkLogs] = useState<NetworkLogEntry[]>([])
  const listenersRef = useRef<Partial<Record<ConsoleLogLevel, ConsoleMethod>>>({})

  const appendConsoleLog = useCallback((entry: ConsoleLogEntry) => {
    setConsoleLogs((prev) => {
      const next = [...prev, entry]
      if (next.length > MAX_LOG_ENTRIES) {
        return next.slice(-MAX_LOG_ENTRIES)
      }
      return next
    })
  }, [])

  const appendNetworkLog = useCallback((entry: NetworkLogEntry) => {
    setNetworkLogs((prev) => {
      const next = [...prev, entry]
      if (next.length > MAX_NETWORK_ENTRIES) {
        return next.slice(-MAX_NETWORK_ENTRIES)
      }
      return next
    })
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const levels: ConsoleLogLevel[] = ['log', 'info', 'warn', 'error']
    const originalMethods: Partial<Record<ConsoleLogLevel, ConsoleMethod>> = {}

    levels.forEach((level) => {
      const original = console[level] as ConsoleMethod
      originalMethods[level] = original

      console[level] = (...args: unknown[]) => {
        try {
          const entry: ConsoleLogEntry = {
            level,
            message: args
              .map((arg) => {
                if (typeof arg === 'string') return arg
                try {
                  return JSON.stringify(arg)
                } catch {
                  return String(arg)
                }
              })
              .join(' '),
            timestamp: new Date().toISOString(),
          }
          appendConsoleLog(entry)
          listenersRef.current[level]?.(...args)
        } catch {
          listenersRef.current[level]?.(...args)
        } finally {
          original(...args)
        }
      }
    })

    return () => {
      levels.forEach((level) => {
        const original = originalMethods[level]
        if (original) {
          console[level] = original
        }
      })
    }
  }, [appendConsoleLog])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const resourceEntries = performance.getEntriesByType('resource') as PerformanceResourceTiming[]
    if (resourceEntries.length) {
      const mapped = resourceEntries.map<NetworkLogEntry>((entry) => ({
        id: createId(),
        type: 'resource',
        url: entry.name,
        method: 'GET',
        status: 'responseStatus' in entry ? (entry as unknown as { responseStatus?: number }).responseStatus : undefined,
        durationMs: entry.duration,
        timestamp: new Date(performance.timeOrigin + entry.startTime).toISOString(),
        initiator: entry.initiatorType,
        transferSize: entry.transferSize,
        encodedBodySize: entry.encodedBodySize,
        decodedBodySize: entry.decodedBodySize,
      }))
      if (mapped.length) {
        setNetworkLogs((prev) => {
          const combined = [...prev, ...mapped]
          return combined.length > MAX_NETWORK_ENTRIES ? combined.slice(-MAX_NETWORK_ENTRIES) : combined
        })
      }
    }

    const resolveMethod = (input: RequestInfo | URL, init?: RequestInit): string => {
      if (init?.method) return init.method.toUpperCase()
      if (typeof input === 'string') return 'GET'
      if (input instanceof Request) return (input.method ?? 'GET').toUpperCase()
      if (input instanceof URL) return 'GET'
      return 'GET'
    }

    const resolveUrl = (input: RequestInfo | URL): string => {
      if (typeof input === 'string') return input
      if (input instanceof Request) return input.url
      if (input instanceof URL) return input.toString()
      return String(input)
    }

    // Native fetch requires `this === window`; keep the raw reference so cleanup
    // restores the exact original identity, but call through a bound copy.
    const rawFetch = window.fetch
    const originalFetch = rawFetch.bind(window)
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const startedAt = performance.now()
      const timestamp = new Date().toISOString()
      const input = args[0]
      const init = args[1]
      const method = resolveMethod(input, init)
      const url = resolveUrl(input)
      try {
        const response = await originalFetch(...args)
        const duration = performance.now() - startedAt
        appendNetworkLog({
          id: createId(),
          type: 'fetch',
          url,
          method,
          status: response.status,
          ok: response.ok,
          durationMs: duration,
          timestamp,
        })
        return response
      } catch (error) {
        const duration = performance.now() - startedAt
        appendNetworkLog({
          id: createId(),
          type: 'fetch',
          url,
          method,
          durationMs: duration,
          timestamp,
          error: error instanceof Error ? error.message : String(error),
        })
        throw error
      }
    }

    const xhrOpen = XMLHttpRequest.prototype.open
    const xhrSend = XMLHttpRequest.prototype.send

    function overrideOpen(this: XMLHttpRequest, method: string, url: string | URL): void
    function overrideOpen(
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null,
    ): void
    function overrideOpen(
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      ...rest: [async?: boolean, username?: string | null, password?: string | null]
    ): void {
      ;(this as XMLHttpRequest & { __method?: string; __url?: string }).__method = (method ?? 'GET').toUpperCase()
      ;(this as XMLHttpRequest & { __url?: string }).__url = typeof url === 'string' ? url : url?.toString() ?? ''
      xhrOpen.call(this, method, url, ...rest)
    }

    XMLHttpRequest.prototype.open = overrideOpen

    XMLHttpRequest.prototype.send = function (...args: Parameters<XMLHttpRequest['send']>) {
      const xhr = this as XMLHttpRequest & { __method?: string; __url?: string }
      const method = xhr.__method ?? 'GET'
      const url = xhr.__url ?? ''
      const timestamp = new Date().toISOString()
      const startedAt = performance.now()

      const finalize = () => {
        const duration = performance.now() - startedAt
        appendNetworkLog({
          id: createId(),
          type: 'xhr',
          url: xhr.responseURL || url,
          method,
          status: xhr.status || undefined,
          ok: xhr.status ? xhr.status >= 200 && xhr.status < 400 : undefined,
          durationMs: duration,
          timestamp,
        })
        xhr.removeEventListener('loadend', finalize)
        xhr.removeEventListener('error', finalize)
        xhr.removeEventListener('abort', finalize)
      }

      xhr.addEventListener('loadend', finalize)
      xhr.addEventListener('error', finalize)
      xhr.addEventListener('abort', finalize)

      return xhrSend.apply(this, args)
    }

    return () => {
      window.fetch = rawFetch
      XMLHttpRequest.prototype.open = xhrOpen
      XMLHttpRequest.prototype.send = xhrSend
    }
  }, [appendNetworkLog])

  const snapshotContext = useCallback((): DiagnosticsSnapshot => {
    if (typeof window === 'undefined') {
      return {
        url: '',
        userAgent: '',
        viewport: '',
        online: false,
        timestamp: new Date().toISOString(),
      }
    }

    const width = window.innerWidth ?? 0
    const height = window.innerHeight ?? 0
    const navigatorInfo = window.navigator

    const memoryInfo =
      typeof performance !== 'undefined' && 'memory' in performance
        ? (performance.memory as MemoryInfo | null)
        : null

    const connectionInfo = (navigatorInfo as Navigator & { connection?: NetworkInformationLike }).connection

    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[]
    const resourceCounts = resources.reduce(
      (acc, entry) => {
        const initiator = entry.initiatorType
        if (initiator === 'script') acc.scripts += 1
        else if (initiator === 'img' || initiator === 'image') acc.images += 1
        else if (initiator === 'fetch' || initiator === 'xmlhttprequest') acc.fetches += 1
        else acc.other += 1
        acc.total += 1
        return acc
      },
      { total: 0, scripts: 0, images: 0, fetches: 0, other: 0 }
    )

    return {
      url: window.location.href,
      userAgent: navigatorInfo.userAgent,
      viewport: `${width}x${height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigatorInfo.language,
      platform: navigatorInfo.platform,
      online: navigatorInfo.onLine,
      referrer: document.referrer,
      memory: memoryInfo
        ? `used ${Math.round(memoryInfo.usedJSHeapSize / 1024 / 1024)}MB / limit ${Math.round(memoryInfo.jsHeapSizeLimit / 1024 / 1024)}MB`
        : undefined,
      memoryStats: memoryInfo
        ? {
            usedJSHeapSize: memoryInfo.usedJSHeapSize,
            totalJSHeapSize: memoryInfo.totalJSHeapSize,
            jsHeapSizeLimit: memoryInfo.jsHeapSizeLimit,
          }
        : undefined,
      connection: connectionInfo
        ? {
            effectiveType: connectionInfo.effectiveType,
            downlink: connectionInfo.downlink,
            rtt: connectionInfo.rtt,
            saveData: connectionInfo.saveData,
          }
        : undefined,
      navigation: navigationEntry
        ? {
            type: navigationEntry.type,
            loadEventEnd: navigationEntry.loadEventEnd,
            domContentLoaded: navigationEntry.domContentLoadedEventEnd,
            domInteractive: navigationEntry.domInteractive,
            transferSize: navigationEntry.transferSize,
            encodedBodySize: navigationEntry.encodedBodySize,
            decodedBodySize: navigationEntry.decodedBodySize,
          }
        : undefined,
      resourceCounts,
      timestamp: new Date().toISOString(),
      tanstackQuerySummary: (() => {
        try {
          const qc = queryClient
          if (!qc) return undefined
          const allQueries = qc.getQueryCache().getAll()
          const activeQueries = allQueries.filter((q) => q.isActive()).length
          const errorQueries = allQueries.filter((q) => q.state.status === 'error').length
          const fetchingQueries = allQueries.filter((q) => q.state.fetchStatus === 'fetching').length
          const queries = allQueries.slice(-10).map((q) => ({
            queryKey: JSON.stringify(q.queryKey),
            state: q.state.status,
            dataUpdatedAt: q.state.dataUpdatedAt || undefined,
          }))
          return { activeQueries, errorQueries, fetchingQueries, queries }
        } catch {
          return undefined
        }
      })(),
      failedRequests: (() => {
        const failed = networkLogs
          .filter((entry) => entry.ok === false || Boolean(entry.error))
          .slice(-20)
          .map((entry) => ({
            url: entry.url,
            method: entry.method,
            status: entry.status,
            error: entry.error,
            timestamp: entry.timestamp,
          }))
        return failed.length > 0 ? failed : undefined
      })(),
    }
  }, [queryClient, networkLogs])

  const resetLogs = useCallback(() => {
    setConsoleLogs([])
    setNetworkLogs([])
  }, [])

  const registerConsoleListener = useCallback((level: ConsoleLogLevel, listener: ConsoleMethod) => {
    listenersRef.current[level] = listener
    return () => {
      delete listenersRef.current[level]
    }
  }, [])

  return useMemo(
    () => ({
      consoleLogs,
      networkLogs,
      snapshotContext,
      resetLogs,
      registerConsoleListener,
    }),
    [consoleLogs, networkLogs, snapshotContext, resetLogs, registerConsoleListener]
  )
}
